/**
 * DCTA-111 — the wait between the customer's words and the bank's answer.
 *
 * With drafting models behind the bank (drafter 2–3 s, validator up to ~11 s, in parallel) a
 * request takes 3–15 s, where the fixture path took under a second. The wait therefore has a
 * shape, and the chat draws each phase (`ChatView.working`):
 *
 *  - **quiet** (0–2.5 s): the typing dots only — a fast answer draws nothing new (§2.3's busy rule:
 *    a short wait gets no new object);
 *  - **slow** (from 2.5 s): one calm line and Cancel;
 *  - **still** (from 10 s): the line says the customer will check the card before anything is sent;
 *  - **late** (from 25 s, DCTA-131 F9, §F.2 3g): the same slot says the wait is longer than usual and keeps
 *    that second sentence (DCTA-217: the slogan "nothing moves until…" is out of the app);
 *  - the app **stops waiting** at 45 s and says so (fail closed: nothing was sent). DCTA-124: the
 *    bank bounds a model call at 40 s (DCTA-123, ADP timeout — the drafter's one retry shares it), so
 *    the app never gives up on a request the bank is still answering in time; 5 s of margin covers
 *    the policy, the payload and the network after it.
 *
 * Cancel and the give-up end the wait for the customer, not the request at the bank: whatever the
 * bank answers afterwards goes to `onLate` (the chat cancels a card nobody waited for, and shows a
 * protective action the bank already ran — the screen never hides the bank's state).
 */
export type WaitPhase = "quiet" | "slow" | "still" | "late";

export const WAIT = { slowAfterMs: 2500, stillAfterMs: 10_000, lateAfterMs: 25_000, giveUpAfterMs: 45_000 } as const;

export type Settled<T> = { readonly kind: "answered"; readonly value: T } | { readonly kind: "failed"; readonly error: unknown };
export type Waited<T> = Settled<T> | { readonly kind: "cancelled" } | { readonly kind: "timed-out" };

export interface WaitOptions<T> {
  /** Every phase as it begins — `quiet` synchronously, before `wait` returns. */
  readonly onPhase: (phase: WaitPhase) => void;
  /** What the bank answered after the customer stopped waiting (Cancel, or the give-up). */
  readonly onLate?: (late: Settled<T>) => void;
}

export interface Wait<T> {
  /** How the wait ended, once. */
  readonly done: Promise<Waited<T>>;
  /** The customer's Cancel. After the wait has ended it does nothing. */
  cancel(): void;
}

export function wait<T>(work: Promise<T>, options: WaitOptions<T>): Wait<T> {
  let ended = false;
  let finish!: (w: Waited<T>) => void;
  const done = new Promise<Waited<T>>((resolve) => {
    finish = resolve;
  });
  const timers = [
    setTimeout(() => phase("slow"), WAIT.slowAfterMs),
    setTimeout(() => phase("still"), WAIT.stillAfterMs),
    setTimeout(() => phase("late"), WAIT.lateAfterMs),
    setTimeout(() => stop({ kind: "timed-out" }), WAIT.giveUpAfterMs),
  ];
  function phase(p: WaitPhase): void {
    if (!ended) options.onPhase(p);
  }
  function end(w: Waited<T>): void {
    ended = true;
    for (const t of timers) clearTimeout(t);
    finish(w);
  }
  /** The customer stopped waiting: what the bank says next is late. */
  function stop(w: Waited<T>): void {
    if (ended) return;
    end(w);
    work.then(
      (value) => options.onLate?.({ kind: "answered", value }),
      (error: unknown) => options.onLate?.({ kind: "failed", error }),
    );
  }
  work.then(
    (value) => {
      if (!ended) end({ kind: "answered", value });
    },
    (error: unknown) => {
      if (!ended) end({ kind: "failed", error });
    },
  );
  options.onPhase("quiet");
  return { done, cancel: () => stop({ kind: "cancelled" }) };
}
